import type {
  AccessEventDto,
  AccessEventsQuery,
  AccessPointDto,
  AccessRuleDto,
  AgentReleaseDto,
  AlertDto,
  AnnouncementDto,
  AnnouncementReadsDto,
  AuditEntryDto,
  BlockDto,
  CallDto,
  CameraDto,
  ChargeDto,
  CondominiumDetail,
  CondominiumOverview,
  CredentialDto,
  DashboardDto,
  DeviceAdminDto,
  DeviceSummaryDto,
  DeviceSyncSummary,
  FinanceSummaryDto,
  FrontDeskOverviewDto,
  GatewayDetail,
  GatewayDto,
  GatewayFleetRow,
  GlobalSearchDto,
  HolidayDto,
  IntercomStationDto,
  InviteDto,
  PackageDto,
  PackagesSummaryDto,
  Paginated,
  ParkingSpaceDto,
  PersonDetailDto,
  PersonDto,
  SecurityOverview,
  SessionSummary,
  UnitDetailDto,
  UnitDto,
  VehicleDto,
  VisitorEntryDto,
  VisitorInviteDto,
  PersonAccessDto,
  CameraViewersDto,
} from '@brimostech/shared';
import { api, qs } from './api-client';

type Params = Record<string, string | number | undefined | null>;

/** Query keys (scoped by tenant where it matters) and fetchers used across the app. */
export const queries = {
  // ---- announcements (phase 10) ----
  announcements: (tenantId: string | null, params: Params) => ({
    queryKey: ['announcements', tenantId, params] as const,
    queryFn: () => api<Paginated<AnnouncementDto>>(`/announcements${qs(params)}`),
  }),
  announcementsWall: (tenantId: string | null) => ({
    queryKey: ['announcements', tenantId, 'wall'] as const,
    queryFn: () => api<AnnouncementDto[]>('/announcements/wall'),
  }),
  announcementReads: (tenantId: string | null, id: string, pending: boolean) => ({
    queryKey: ['announcements', tenantId, 'reads', id, pending] as const,
    queryFn: () =>
      api<AnnouncementReadsDto>(`/announcements/${id}/reads${qs({ pending: String(pending) })}`),
  }),
  // ---- finance (phase 12) ----
  charges: (tenantId: string | null, params: Params) => ({
    queryKey: ['charges', tenantId, params] as const,
    queryFn: () => api<Paginated<ChargeDto>>(`/charges${qs(params)}`),
  }),
  financeSummary: (tenantId: string | null, competence: string) => ({
    queryKey: ['charges', tenantId, 'summary', competence] as const,
    queryFn: () => api<FinanceSummaryDto>(`/charges/summary?competence=${competence}`),
  }),
  // ---- packages (phase 9) ----
  packages: (tenantId: string | null, params: Params) => ({
    queryKey: ['packages', tenantId, params] as const,
    queryFn: () => api<Paginated<PackageDto>>(`/packages${qs(params)}`),
  }),
  packagesSummary: (tenantId: string | null) => ({
    queryKey: ['packages', tenantId, 'summary'] as const,
    queryFn: () => api<PackagesSummaryDto>('/packages/summary'),
  }),
  // ---- visitors (phase 8) ----
  visitorInvites: (tenantId: string | null, params: Params) => ({
    queryKey: ['visitor-invites', tenantId, params] as const,
    queryFn: () => api<Paginated<VisitorInviteDto>>(`/visitors/invites${qs(params)}`),
  }),
  // ---- dashboard, audit, search (phase 7) ----
  dashboard: (tenantId: string | null) => ({
    queryKey: ['dashboard', tenantId] as const,
    queryFn: () => api<DashboardDto>('/dashboard'),
  }),
  audit: (tenantId: string | null, params: Params) => ({
    queryKey: ['audit', tenantId, params] as const,
    queryFn: () => api<Paginated<AuditEntryDto>>(`/audit${qs(params)}`),
  }),
  auditActions: (tenantId: string | null) => ({
    queryKey: ['audit', tenantId, 'actions'] as const,
    queryFn: () => api<string[]>('/audit/actions'),
  }),
  search: (tenantId: string | null, q: string) => ({
    queryKey: ['search', tenantId, q] as const,
    queryFn: () => api<GlobalSearchDto>(`/search${qs({ q })}`),
  }),
  // ---- intercom / front desk ----
  activeCalls: (tenantId: string | null) => ({
    queryKey: ['calls', tenantId, 'active'] as const,
    queryFn: () => api<CallDto[]>('/calls/active'),
  }),
  calls: (tenantId: string | null, params: Params) => ({
    queryKey: ['calls', tenantId, params] as const,
    queryFn: () => api<Paginated<CallDto>>(`/calls${qs(params)}`),
  }),
  stations: (tenantId: string | null) => ({
    queryKey: ['stations', tenantId] as const,
    queryFn: () => api<IntercomStationDto[]>('/intercom/stations'),
  }),
  frontDeskOverview: (tenantId: string | null) => ({
    queryKey: ['frontdesk', tenantId, 'overview'] as const,
    queryFn: () => api<FrontDeskOverviewDto>('/frontdesk/overview'),
  }),
  frontDeskVisitors: (tenantId: string | null) => ({
    queryKey: ['frontdesk', tenantId, 'visitors'] as const,
    queryFn: () => api<VisitorEntryDto[]>('/frontdesk/visitors'),
  }),
  condominiumOverview: (tenantId: string | null) => ({
    queryKey: ['condominium', tenantId, 'overview'] as const,
    queryFn: () => api<CondominiumOverview>('/condominiums/current'),
  }),
  condominiumDetail: (tenantId: string | null) => ({
    queryKey: ['condominium', tenantId, 'detail'] as const,
    queryFn: () => api<CondominiumDetail>('/condominiums/current/detail'),
  }),
  blocks: (tenantId: string | null) => ({
    queryKey: ['blocks', tenantId] as const,
    queryFn: () => api<BlockDto[]>('/blocks'),
  }),
  units: (tenantId: string | null, params: Params) => ({
    queryKey: ['units', tenantId, params] as const,
    queryFn: () => api<Paginated<UnitDto>>(`/units${qs(params)}`),
  }),
  unit: (tenantId: string | null, id: string) => ({
    queryKey: ['units', tenantId, 'detail', id] as const,
    queryFn: () => api<UnitDetailDto>(`/units/${id}`),
  }),
  persons: (tenantId: string | null, params: Params) => ({
    queryKey: ['persons', tenantId, params] as const,
    queryFn: () => api<Paginated<PersonDto>>(`/persons${qs(params)}`),
  }),
  person: (tenantId: string | null, id: string) => ({
    queryKey: ['persons', tenantId, 'detail', id] as const,
    queryFn: () => api<PersonDetailDto>(`/persons/${id}`),
  }),
  vehicles: (tenantId: string | null, params: Params) => ({
    queryKey: ['vehicles', tenantId, params] as const,
    queryFn: () => api<Paginated<VehicleDto>>(`/vehicles${qs(params)}`),
  }),
  parking: (tenantId: string | null) => ({
    queryKey: ['parking', tenantId] as const,
    queryFn: () => api<ParkingSpaceDto[]>('/parking-spaces'),
  }),
  credentials: (tenantId: string | null, params: Params) => ({
    queryKey: ['credentials', tenantId, params] as const,
    queryFn: () => api<CredentialDto[]>(`/credentials${qs(params)}`),
  }),
  personAccess: (tenantId: string | null, personId: string) => ({
    queryKey: ['person-access', tenantId, personId] as const,
    queryFn: () => api<PersonAccessDto>(`/persons/${personId}/access`),
  }),
  accessPoints: (tenantId: string | null) => ({
    queryKey: ['access-points', tenantId] as const,
    queryFn: () => api<AccessPointDto[]>('/access-points'),
  }),
  cameraViewers: (tenantId: string | null) => ({
    queryKey: ['camera-viewers', tenantId] as const,
    queryFn: () => api<CameraViewersDto>('/cameras/viewers'),
  }),
  cameras: (tenantId: string | null) => ({
    queryKey: ['cameras', tenantId] as const,
    queryFn: () => api<CameraDto[]>('/cameras'),
  }),
  devices: (tenantId: string | null) => ({
    queryKey: ['devices', tenantId] as const,
    queryFn: () => api<DeviceSummaryDto[]>('/devices'),
  }),
  gateways: (tenantId: string | null) => ({
    queryKey: ['gateways', tenantId] as const,
    queryFn: () => api<GatewayDto[]>('/gateways'),
  }),
  invites: (tenantId: string | null, params: Params) => ({
    queryKey: ['invites', tenantId, params] as const,
    queryFn: () => api<InviteDto[]>(`/invites${qs(params)}`),
  }),
  security: () => ({
    queryKey: ['me', 'security'] as const,
    queryFn: () => api<SecurityOverview>('/me/security'),
  }),
  sessions: () => ({
    queryKey: ['me', 'sessions'] as const,
    queryFn: () => api<SessionSummary[]>('/auth/sessions'),
  }),
  accessRules: (tenantId: string | null) => ({
    queryKey: ['access-rules', tenantId] as const,
    queryFn: () => api<AccessRuleDto[]>('/access-rules'),
  }),
  holidays: (tenantId: string | null) => ({
    queryKey: ['holidays', tenantId] as const,
    queryFn: () => api<HolidayDto[]>('/holidays'),
  }),
  accessEvents: (tenantId: string | null, params: Partial<AccessEventsQuery>) => ({
    queryKey: ['access-events', tenantId, params] as const,
    queryFn: () => api<Paginated<AccessEventDto>>(`/access-events${qs(params as Params)}`),
  }),
  syncSummaries: (tenantId: string | null) => ({
    queryKey: ['sync', tenantId, 'devices'] as const,
    queryFn: () => api<DeviceSyncSummary[]>('/sync/devices'),
  }),
  alerts: (tenantId: string | null, status: 'open' | 'resolved' | 'all') => ({
    queryKey: ['alerts', tenantId, status] as const,
    queryFn: () => api<AlertDto[]>(`/alerts${qs({ status })}`),
  }),
  // ---- operator (superadmin) ----
  fleet: () => ({
    queryKey: ['admin', 'gateways'] as const,
    queryFn: () => api<GatewayFleetRow[]>('/admin/gateways'),
  }),
  gatewayDetail: (id: string) => ({
    queryKey: ['admin', 'gateways', id] as const,
    queryFn: () => api<GatewayDetail>(`/admin/gateways/${id}`),
  }),
  adminDevices: (params: Params) => ({
    queryKey: ['admin', 'devices', params] as const,
    queryFn: () => api<DeviceAdminDto[]>(`/admin/devices${qs(params)}`),
  }),
  releases: () => ({
    queryKey: ['admin', 'releases'] as const,
    queryFn: () => api<AgentReleaseDto[]>('/admin/releases'),
  }),
  adminAlerts: () => ({
    queryKey: ['admin', 'alerts'] as const,
    queryFn: () => api<AlertDto[]>('/admin/alerts'),
  }),
};
